import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { userApi } from '../services/api';
import RecommendationContent from '../components/RecommendationContent';
import SubscriptionManager from '../components/SubscriptionManager';

function AIRecommendation({ isLoggedIn }) {
  const [activeModule, setActiveModule] = useState('suck');
  const [activeSubTab, setActiveSubTab] = useState({
    suck: 'panel-low',
    wash: 'panel-fake-down',
    rise: 'panel-fast-rise',
    sell: 'panel-fake-break-sell',
    'must-see': ''
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [subscriptions, setSubscriptions] = useState({
    suck: false,
    wash: false,
    rise: false,
    sell: false
  });
  const [loading, setLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      setLoading(true);
      
      try {
        if (isLoggedIn) {
          const response = await userApi.getUserSubscriptions();
          if (response.success) {
            setSubscriptions(response.data);
          } else {
            console.error('Failed to fetch subscriptions:', response.message);
          }
        } else {
          setSubscriptions({
            suck: false,
            wash: false,
            rise: false,
            sell: false
          });
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscriptions();
    
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  }, [isLoggedIn]);

  const handleModuleChange = (module) => {
    setActiveModule(module);
  };

  const handleSubTabChange = (tabId) => {
    setActiveSubTab({
      ...activeSubTab,
      [activeModule]: tabId
    });
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleLoginRequired = () => {
    setShowLoginPrompt(true);
  };

  const handleLogin = () => {
    setShowLoginPrompt(false);
    const loginEvent = new CustomEvent('showLogin');
    window.dispatchEvent(loginEvent);
  };

  const moduleNames = {
    'must-see': '主力必看',
    suck: '吸筹阶段',
    wash: '洗盘阶段',
    rise: '拉升阶段',
    sell: '出货阶段'
  };

  const moduleTargets = {
    'must-see': '总结：吸筹、洗盘、拉升、出货阶段的核心逻辑及散户应对策略',
    suck: '目标：在低位阶段收集足够筹码，而不引起市场关注',
    wash: '目标：清理意志不坚定的散户，为后续拉升做好准备',
    rise: '目标：快速拉高股价，吸引散户进场，推高市场情绪',
    sell: '目标：在高位出货，将筹码卖给接盘散户，实现利润最大化'
  };

  const subTabs = {
    suck: [
      { id: 'panel-low', name: '低调建仓' },
      { id: 'panel-press', name: '打压股价' },
      { id: 'panel-cross', name: '对倒交易' }
    ],
    wash: [
      { id: 'panel-fake-down', name: '假跌破' },
      { id: 'panel-shock', name: '震荡整理' },
      { id: 'panel-fake-break', name: '假突破' }
    ],
    rise: [
      { id: 'panel-fast-rise', name: '快速拉升' },
      { id: 'panel-volume-break', name: '放量突破' },
      { id: 'panel-good-news', name: '释放利好' }
    ],
    sell: [
      { id: 'panel-fake-break-sell', name: '假突破吸引接盘' },
      { id: 'panel-cross-sell', name: '对倒掩护出货' }
    ],
    'must-see': []
  };

  const panelContent = {
    'panel-low': {
      features: [
        '交易量保持在20日均量的50%以内',
        '大单买入分批进场',
        '股价震荡横盘，主力以低价增仓',
        '筹码平衡，主力持仓比例增加'
      ],
      operations: [
        '窄幅震荡，便于吸筹',
        '暗中建仓',
        '成交量平稳',
        '中长期不作赢利测算'
      ]
    },
    'panel-press': {
      features: [
        '利用大卖单引导价格下行',
        '制造短期熊市假象',
        '提供低价买入机会',
        '为后续反弹做准备'
      ],
      operations: [
        '价格大幅波动',
        '诱发恐慌情绪',
        '低吸高抛',
        '控制市场节奏'
      ]
    },
    'panel-cross': {
      features: [
        '自买自卖制造交易假象',
        '维持股价稳定',
        '掩盖真实建仓意图',
        '避免引起市场关注'
      ],
      operations: [
        '交易量放大但筹码未实质流动',
        '价格小幅波动',
        '主力资金暗中流入',
        '散户难以察觉'
      ]
    },
    'panel-fake-down': {
      features: [
        '突然跌破支撑位，引发恐慌性抛售',
        '止损机制触发卖出',
        '成交量放大，主力资金未流出'
      ],
      operations: [
        '大单砸盘制造恐慌',
        '短期内快速回升',
        '清洗散户后企稳'
      ]
    },
    'panel-shock': {
      features: [
        '股价在区间内反复波动',
        '成交量逐步减少',
        '主力暗中吸纳筹码'
      ],
      operations: [
        '制造假突破或假跌破',
        '散户频繁操作亏损',
        '清洗后进入下一阶段'
      ]
    },
    'panel-fake-break': {
      features: [
        '短暂拉升后迅速回落',
        '散户误判上涨趋势',
        '主力趁机清洗高位接盘'
      ],
      operations: [
        '放量诱多',
        '快速回落制造恐慌',
        '低位吸筹'
      ]
    },
    'panel-fast-rise': {
      features: [
        '连续多日上涨，形成阳线趋势',
        '主力资金迅速流入',
        '散户跟风情绪高涨'
      ],
      operations: [
        '大单买入制造上涨动力',
        '突破关键阻力位',
        '吸引追高资金'
      ]
    },
    'panel-volume-break': {
      features: [
        '成交量显著放大',
        '突破前期高点',
        '形成上涨趋势'
      ],
      operations: [
        '制造“牛市”假象',
        '吸引短期资金入场',
        '配合技术指标信号'
      ]
    },
    'panel-good-news': {
      features: [
        '配合重大利好消息',
        '市场情绪被迅速点燃',
        '股价短期大幅上涨'
      ],
      operations: [
        '利用消息面推动股价',
        '媒体宣传扩大影响',
        '吸引更多散户跟进'
      ]
    },
    'panel-fake-break-sell': {
      features: [
        '假装继续拉升吸引散户',
        '随后迅速砸盘，形成高位套牢'
      ],
      operations: [
        '高位放量，主力逐步减仓',
        '制造市场活跃假象',
        '最后突然跳水完成收割'
      ]
    },
    'panel-cross-sell': {
      features: [
        '小单买入、大单卖出制造假象',
        '掩盖主力出货动作',
        '股价滞涨，成交量异常'
      ],
      operations: [
        '主力逐步减仓，避免暴跌',
        '维持表面稳定',
        '最后突然跳水收割散户'
      ]
    }
  };

  const mustSeeContent = {
    suck: {
      mistake: '误区：过早卖出',
      solution: '应对：关注低位横盘，结合成交量'
    },
    wash: {
      mistake: '误区：恐慌割肉',
      solution: '应对：观察假跌破后快速回升，耐心持仓'
    },
    rise: {
      mistake: '误区：追涨买入',
      solution: '应对：分批建仓，关注回调机会'
    },
    sell: {
      mistake: '误区：盲目跟风',
      solution: '应对：高位放量滞涨时逐步减仓'
    }
  };

  return (
    <div>
      <div className="main-nav">
        <div className="container">
          <ul className="main-nav-list">
            <li><a href="#" onClick={(e) => { e.preventDefault(); handleModuleChange('must-see'); }}>庄家操作解码：</a></li>
            <li>
              <a 
                href="#" 
                className={activeModule === 'suck' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handleModuleChange('suck'); }}
              >
                一 吸筹（建仓）
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activeModule === 'wash' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handleModuleChange('wash'); }}
              >
                二 洗盘
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activeModule === 'rise' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handleModuleChange('rise'); }}
              >
                三 拉升
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activeModule === 'sell' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handleModuleChange('sell'); }}
              >
                四 出货
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="container">
        <div className="breadcrumbs">
          <Link to="/">首页</Link> <span>&gt;</span> <span>{moduleNames[activeModule]}</span>
        </div>

        <div className="tab-content active">
          <div className="section-card">
            <div className="section-header">
              {activeModule === 'must-see' 
                ? '主力必看：庄家操作全解码' 
                : `第${
                    activeModule === 'suck' ? '一' : 
                    activeModule === 'wash' ? '二' : 
                    activeModule === 'rise' ? '三' : '四'
                  }阶段：${moduleNames[activeModule]}`
              }
            </div>
            <div className="section-content">
              <div className="section-target">
                {moduleTargets[activeModule]}
              </div>

              {activeModule !== 'must-see' && (
                <>
                  <div className="tab-nav">
                    {subTabs[activeModule].map(tab => (
                      <button 
                        key={tab.id}
                        className={`tab-button ${activeSubTab[activeModule] === tab.id ? 'active' : ''}`}
                        onClick={() => handleSubTabChange(tab.id)}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>

                  {subTabs[activeModule].map(tab => (
                    <div 
                      key={tab.id}
                      id={tab.id} 
                      className={`content-panel ${activeSubTab[activeModule] === tab.id ? 'active' : ''}`}
                    >
                      <div className="panel-column">
                        <h3 className="panel-title">{tab.name} - 特征说明</h3>
                        <ul className="panel-list">
                          {panelContent[tab.id]?.features.map((feature, index) => (
                            <li key={index}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="panel-column">
                        <h3 className="panel-title">操作特点</h3>
                        <ul className="panel-list">
                          {panelContent[tab.id]?.operations.map((operation, index) => (
                            <li key={index}>{operation}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {activeModule === 'must-see' && (
                <>
                  <div className="content-panel active">
                    <div className="panel-column">
                      <h3 className="panel-title">吸筹阶段 - 散户误区及应对</h3>
                      <ul className="panel-list">
                        <li>{mustSeeContent.suck.mistake}</li>
                        <li>{mustSeeContent.suck.solution}</li>
                      </ul>
                    </div>
                    <div className="panel-column">
                      <h3 className="panel-title">洗盘阶段 - 散户误区及应对</h3>
                      <ul className="panel-list">
                        <li>{mustSeeContent.wash.mistake}</li>
                        <li>{mustSeeContent.wash.solution}</li>
                      </ul>
                    </div>
                  </div>
                  <div className="content-panel active">
                    <div className="panel-column">
                      <h3 className="panel-title">拉升阶段 - 散户误区及应对</h3>
                      <ul className="panel-list">
                        <li>{mustSeeContent.rise.mistake}</li>
                        <li>{mustSeeContent.rise.solution}</li>
                      </ul>
                    </div>
                    <div className="panel-column">
                      <h3 className="panel-title">出货阶段 - 散户误区及应对</h3>
                      <ul className="panel-list">
                        <li>{mustSeeContent.sell.mistake}</li>
                        <li>{mustSeeContent.sell.solution}</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
                <a href="#" className="upgrade-button">
                          会员查看详情
                        </a>
              <div className="contentSection">
                {activeModule !== 'must-see' && (
                  <>
                    {subscriptions[activeModule] ? (
                      <RecommendationContent 
                        moduleId={activeModule} 
                        isSubscribed={subscriptions[activeModule]} 
                        date={selectedDate}
                        isLoggedIn={isLoggedIn}
                        onLoginRequired={handleLoginRequired}
                      />
                    ) : (
                      <SubscriptionManager 
                        moduleId={activeModule} 
                        onSubscriptionSuccess={() => {
                          setSubscriptions({
                            ...subscriptions,
                            [activeModule]: true
                          });
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showLoginPrompt && (
        <div className="modal" style={{ display: 'block' }}>
          <div className="modal-content">
            <h2>需要登录</h2>
            <p>请先登录后再进行此操作</p>
            <button onClick={handleLogin}>登录</button>
            <button onClick={() => setShowLoginPrompt(false)}>关闭</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIRecommendation;